import React, { useEffect, useState } from "react";
import Dashboard from "../../layouts/Dashboard";
import { ReactLoading } from "../Dashboard/TinManagement/import";
import {
  Card,
  Select,
  Input,
  Radio,
  message,
  Breadcrumb,
  DatePicker,
  Modal,
  Button,
} from "antd";
import useAxios from "../../hooks/useAxios";
import { getAllUsers } from "../../services/users/users.service";
import Conditions from "./components/Conditions";
import {
  PlusOutlined,
  SaveOutlined,
  ExclamationCircleFilled,
  QuestionCircleFilled,
  SwapOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import { useParams, Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { checkPermission } from "../../utils/checkPermission";
import useRole from "../../hooks/useRole";

const OPTIONS = [
  {
    label: "Active",
    value: true,
  },
  {
    label: "Inactive",
    value: false,
  },
];

const RuleEngineCreate = () => {
  const api = useAxios();
  const history = useHistory();
  const role = useRole();

  const { id, clone } = useParams();

  const [loading, setLoading] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [ruleName, setRuleName] = useState("");
  const [description, setDescription] = useState("");
  const [recruiter, setRecruiter] = useState([]);
  const [status, setStatus] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [conditionErrors, setConditionErrors] = useState([]);
  const [errors, setErrors] = useState(null);
  const [canTransferLeads, setCanTransferLeads] = useState(false);

  useEffect(() => {
    const fetchTransferPermission = async () => {
      if (!role) return;
      try {
        const allowed = await checkPermission(role, "rule_engine_module", "transfer_leads");
        setCanTransferLeads(allowed);
      } catch (err) {
        console.error("Permission check failed:", err);
      }
    };
    fetchTransferPermission();
  }, [role]);

  // States for recruiter lead checks and deletion modals
  const [ruleLeadCounts, setRuleLeadCounts] = useState({});
  const [transferPromptModal, setTransferPromptModal] = useState({
    visible: false,
    recruiterId: null,
    recruiterName: "",
    leadCount: 0,
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    visible: false,
    recruiterId: null,
    recruiterName: "",
  });

  const getRecruiters = async () => {
    try {
      const { data } = await getAllUsers(api, "user");
      setRecruiters(data);
    } catch (err) {
      console.error(err);
      throw new Error("API Failed, Try again later.");
    }
  };

  const getSingleRule = async (id) => {
    try {
      const { data } = await api.get(`/ruleEngine/${id}`, {
        tokenType: "user",
      });
      const { recruiter, ruleName, isActive, groups, description, dateRange } = data;
      setRecruiter(recruiter?.map((ele) => ele?._id));
      setDescription(description);
      setRuleName(ruleName);
      setStatus(isActive);
      setConditions(groups);
    } catch (err) {
      console.error(err);
      throw new Error("API Failed, Try again later.");
    }
  };

  // Fetch lead counts per recruiter for this rule
  const fetchRuleLeadCounts = async (ruleId) => {
    try {
      const { data } = await api.get(`/ruleEngine/recruiterLeadsCount/${ruleId}`, {
        tokenType: "user",
      });
      const counts = {};
      if (data?.assignedRecruiters && Array.isArray(data.assignedRecruiters)) {
        data.assignedRecruiters.forEach((r) => {
          const rId = r?.recruiterId || r?._id || r?.id;
          if (rId) {
            counts[String(rId)] = r?.leadCount || 0;
          }
        });
      }
      setRuleLeadCounts(counts);
    } catch (err) {
      console.warn("Could not fetch recruiter lead counts:", err);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const promise = [getRecruiters()];

        if (id || clone) {
          promise.push(getSingleRule(id || clone));
        }

        if (id) {
          promise.push(fetchRuleLeadCounts(id));
        }

        await Promise.all(promise);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [id, clone]);

  // Handle deselecting a recruiter with lead validation
  const handleRecruiterDeselect = async (removedId) => {
    const recruiterObj = recruiters.find(
      (u) => String(u?._id || u?.id) === String(removedId)
    );
    const recruiterName = recruiterObj?.name || "Selected Recruiter";

    let leadCount = ruleLeadCounts[String(removedId)] || 0;

    // Check latest count from backend if in edit mode
    if (id) {
      try {
        const { data } = await api.get(`/ruleEngine/recruiterLeadsCount/${id}`, {
          tokenType: "user",
        });
        if (data?.assignedRecruiters && Array.isArray(data.assignedRecruiters)) {
          const matching = data.assignedRecruiters.find(
            (r) => String(r?.recruiterId || r?._id || r?.id) === String(removedId)
          );
          leadCount = matching?.leadCount || 0;

          const updatedCounts = {};
          data.assignedRecruiters.forEach((r) => {
            const rId = r?.recruiterId || r?._id || r?.id;
            if (rId) updatedCounts[String(rId)] = r?.leadCount || 0;
          });
          setRuleLeadCounts(updatedCounts);
        } else {
          leadCount = 0;
        }
      } catch (err) {
        console.warn("Using cached lead count:", err);
      }
    }

    if (leadCount > 0) {
      // Recruiter has active leads: show modal with link to transfer leads page
      setTransferPromptModal({
        visible: true,
        recruiterId: removedId,
        recruiterName,
        leadCount,
      });
    } else {
      // Recruiter has 0 leads: show delete confirmation modal
      setDeleteConfirmModal({
        visible: true,
        recruiterId: removedId,
        recruiterName,
      });
    }
  };

  const handleRecruiterChange = async (newValues) => {
    // If not in edit mode (e.g. creating new rule), update directly
    if (!id) {
      setRecruiter(newValues);
      return;
    }

    // If new recruiter(s) added
    if (newValues.length >= recruiter.length) {
      setRecruiter(newValues);
      return;
    }

    // If recruiter(s) removed
    const removedIds = recruiter.filter((rId) => !newValues.includes(rId));
    if (removedIds.length > 0) {
      const removedId = removedIds[0];
      await handleRecruiterDeselect(removedId);
    }
  };

  const confirmDeleteRecruiter = () => {
    if (deleteConfirmModal.recruiterId) {
      setRecruiter((prev) =>
        prev.filter(
          (rId) => String(rId) !== String(deleteConfirmModal.recruiterId)
        )
      );
    }
    setDeleteConfirmModal({
      visible: false,
      recruiterId: null,
      recruiterName: "",
    });
  };

  const addGroup = () => {
    setConditions((prev) => {
      return [
        ...prev.map((ele) => ({
          ...ele,
          relation: "OR",
        })),
        {
          _id: uuid(),
          conditions: [
            {
              _id: uuid(),
              label: "",
              operator: "",
              value: "",
              relation: "",
              order: 0,
            },
          ],
          relation: "",
          order: prev?.length || 0,
        },
      ];
    });
  };

  const validateConditions = (groups) => {
    const errors = [];

    for (const group of groups) {
      for (let i = 0; i < group.conditions.length; i++) {
        const con = group.conditions[i];

        if (!con.label) {
          errors.push({
            field: "label",
            conditionId: con._id,
            groupId: group._id,
            message: "Parameter is required",
          });
        }

        if (!con.operator) {
          errors.push({
            field: "operator",
            conditionId: con._id,
            groupId: group._id,
            message: "Operator is required",
          });
        }

        if (!con.value || (Array.isArray(con.value) && con.value.length === 0)) {
          errors.push({
            field: "value",
            conditionId: con._id,
            groupId: group._id,
            message: "Value is required",
          });
        }

        if (i !== group.conditions.length - 1 && !con.relation) {
          errors.push({
            field: "relation",
            conditionId: con._id,
            groupId: group._id,
            message: "Relation (AND/OR) is required",
          });
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  };

  const saveRule = async (key, ruleId) => {
    const result = validateConditions(conditions);
    if (
      !result.valid ||
      recruiter.length === 0 ||
      !ruleName ||
      conditions.length === 0
    ) {
      setConditionErrors(result.errors);
      let e = {};
      if (recruiter.length === 0) {
        e.recruiter = "Recruiter is required.";
      }

      if (!ruleName) {
        e.ruleName = "Rule name is required.";
      }

      if (conditions.length === 0) {
        e.conditions = "Condition is required.";
      }

      setErrors(e);
      message.error("Please fix highlighted fields");
      return;
    }
    setErrors(null);
    setConditionErrors([]);

    try {
      if (key === "Add" || key === "Clone") {
        await api.post(
          `/ruleEngine`,
          {
            groups: conditions,
            recruiter: recruiter,
            description: description,
            ruleName: ruleName,
            isActive: status,
          },
          {
            tokenType: "user",
          },
        );
        message.success(`Rule ${key} Successfully.`);
      }

      if (key === "Update" && ruleId) {
        await api.put(
          `/ruleEngine/${ruleId}`,
          {
            groups: conditions,
            recruiter: recruiter,
            description: description,
            ruleName: ruleName,
            isActive: status,
          },
          {
            tokenType: "user",
          },
        );
        message.success("Rule Updated Successfully.");
      }

      if (history.location.state?.fromUsers) {
        history.push("/users");
      } else {
        history.push(`/rule-engine`);
      }
    } catch (err) {
      console.error(err);
      const error = err?.response?.data?.error;
      const errMessage = err?.response?.data?.message;
      message.error(
        errMessage || error ? errMessage || error : "Something went wrong.",
      );
    }
  };

  console.log(conditions)

  const hasRecruiterError = errors?.recruiter || "";
  const hasRuleNameError = errors?.ruleName || "";
  const hasDescriptionError = errors?.description || "";
  const hasConditionError = errors?.conditions || "";

  const breadcrumbs = [
    {
      label: "Rule Engine",
      url: `/rule-engine`,
    },
    {
      label: clone ? "Clone Rule" : id ? ruleName : "Create Rule",
      url: "",
    },
  ];

  return (
    <Dashboard title="Rule Engine">
      {loading && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}
      <div className="py-1 px-1">
        <Breadcrumb>
          {breadcrumbs?.map((item) => {
            return (
              <Breadcrumb.Item>
                {item?.url !== "" ? (
                  <Link
                    style={{
                      color: "black",
                      textDecoration: "underline",
                    }}
                    to={item?.url}
                  >
                    {item?.label}
                  </Link>
                ) : (
                  <div className="text-capitalize">{item?.label}</div>
                )}
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
        <h4 className="text-[1.5rem] mb-0">
          {clone ? "Clone" : id ? "Update" : "Create"} Rule Engine
        </h4>
      </div>
      <div className="mt-4">
        <Card>
          <div className="flex flex-col gap-[1.5rem]">
            <div style={!status ? { pointerEvents: "none", opacity: 0.7 } : {}}>
              <Card
                style={
                  hasConditionError
                    ? {
                      border: "1px solid red",
                    }
                    : {}
                }
                title="Condition"
                extra={
                  <div className="flex gap-1">
                    <button
                      disabled={!status}
                      onClick={(e) => addGroup()}
                      className={`btn-sm btn-primary ${!status ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <PlusOutlined /> Add Rule
                    </button>
                  </div>
                }
              >
                <div className="flex flex-col items-center justify-center">
                  {conditions?.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <span className="text-sm">No conditions added yet</span>
                      <span className="text-xs text-gray-400">
                        Click "Add Rule" to create one
                      </span>
                    </div>
                  ) : (
                    <div className="w-full">
                      <Conditions
                        conditions={conditions}
                        setConditions={setConditions}
                        conditionErrors={conditionErrors}
                        disabled={!status}
                      />
                    </div>
                  )}
                </div>
              </Card>
              {hasConditionError && (
                <p className="text-red-500 !mb-0">Condition is required</p>
              )}
            </div>
            <Card title="Recruiter" style={!status ? { pointerEvents: "none", opacity: 0.7 } : {}}>
              <div className="flex items-center">
                <div className="w-[200px]">
                  <label className="text-[14px]">Select Recruiters:</label>
                </div>
                <div className="w-[100%]">
                  <Select
                    disabled={!status}
                    status={hasRecruiterError ? "error" : ""}
                    showSearch
                    mode="multiple"
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement
                    }
                    value={recruiter}
                    className="w-100 p-0 outline-none bg-white increase_drop"
                    placeholder="e.g. John Doe"
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const search = input.toLowerCase();
                      return (
                        String(option?.children ?? "")
                          .toLowerCase()
                          .includes(search) ||
                        String(option?.value ?? "")
                          .toLowerCase()
                          .includes(search)
                      );
                    }}
                    onChange={handleRecruiterChange}
                  >
                    {recruiters
                      ?.sort((a, b) =>
                        a?.name
                          ?.trim()
                          .toLowerCase()
                          .localeCompare(b?.name?.trim().toLowerCase()),
                      )
                      ?.map((user, index) => {
                        if (user?.role === "interviewer" && user.IsUserActive) {
                          return (
                            <Select.Option key={index} value={user?._id}>
                              {user?.name}
                            </Select.Option>
                          );
                        }
                      })}
                  </Select>
                  {hasRecruiterError && (
                    <p className="text-red-500 !mb-0">
                      Recruiter Name is required
                    </p>
                  )}
                </div>
              </div>
            </Card>
            <Card title="General Information">
              <div style={!status ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                <div className="flex items-center">
                  <div className="w-[200px]">
                    <label className="text-[14px]">Rule Name:</label>
                  </div>
                  <div className="w-[100%]">
                    <Input
                      disabled={!status}
                      status={hasRuleNameError ? "error" : ""}
                      type="text"
                      placeholder="e.g. Final Interview"
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                    />
                    {hasRuleNameError && (
                      <p className="text-red-500 mb-0">Rule Name is required</p>
                    )}
                  </div>
                </div>
                <br></br>
                <div className="flex items-center">
                  <div className="w-[200px]">
                    <label className="text-[14px]">Purpose/Description:</label>
                  </div>
                  <div className="w-[100%]">
                    <Input
                      disabled={!status}
                      status={hasDescriptionError ? "error" : ""}
                      type="text"
                      placeholder="e.g. Priority"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                    {hasDescriptionError && (
                      <p className="text-red-500 mb-0">Description is required</p>
                    )}
                  </div>
                </div>
              </div>
              <br></br>
              <div className="flex items-center">
                <div className="w-[200px]">
                  <label className="text-[14px]">Status:</label>
                </div>
                <div className="w-[100%]">
                  <Radio.Group
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    block
                    options={OPTIONS}
                  />
                </div>
              </div>
              <br></br>
              <div className="flex items-center justify-center">
                <button
                  onClick={(e) =>
                    saveRule(
                      clone ? "Clone" : id ? "Update" : "Add",
                      id || clone,
                    )
                  }
                  data-testid="add-new"
                  className="btn btn-primary flex gap-[10px] items-center"
                >
                  <SaveOutlined
                    style={{ fontSize: "1.1rem", fontWeight: 600 }}
                  />
                  <span className="flex items-center text-[16px] font-semibold">
                    {clone ? "Clone Rule" : id ? "Update Rule" : "Save Rule"}
                  </span>
                </button>
              </div>
            </Card>
          </div>
        </Card>
      </div>

      {/* Modal when Recruiter has active leads */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-amber-600 font-bold">
            <ExclamationCircleFilled style={{ color: "#FA8C16", fontSize: "20px" }} />
            <span>Active Leads Found</span>
          </div>
        }
        open={transferPromptModal.visible}
        onCancel={() =>
          setTransferPromptModal({
            visible: false,
            recruiterId: null,
            recruiterName: "",
            leadCount: 0,
          })
        }
        footer={[
          <Button
            key="cancel"
            onClick={() =>
              setTransferPromptModal({
                visible: false,
                recruiterId: null,
                recruiterName: "",
                leadCount: 0,
              })
            }
          >
            Cancel
          </Button>,
          canTransferLeads && (
            <Button
              key="transfer"
              type="primary"
              style={{ backgroundColor: "#FA8C16", borderColor: "#FA8C16" }}
              onClick={() => {
                const ruleIdToTransfer = id;
                setTransferPromptModal({
                  visible: false,
                  recruiterId: null,
                  recruiterName: "",
                  leadCount: 0,
                });
                history.push(`/rule-engine/transfer/${ruleIdToTransfer}`);
              }}
            >
              Go to Transfer Leads
            </Button>
          ),
        ].filter(Boolean)}
      >
        <div className="py-3">
          <p className="text-sm text-gray-800 mb-2">
            <strong>{transferPromptModal.recruiterName}</strong> currently has{" "}
            <span className="font-bold text-orange-600">
              {transferPromptModal.leadCount} active lead{transferPromptModal.leadCount > 1 ? "s" : ""}
            </span>{" "}
            assigned under this rule.
          </p>
          <p className="text-xs text-gray-500 mb-0">
            Recruiters with active leads cannot be deleted directly. Please transfer their existing leads to another recruiter first.
          </p>
        </div>
      </Modal>

      {/* Confirmation Modal when Recruiter has 0 leads */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-gray-800 font-bold">
            <QuestionCircleFilled style={{ color: "#1890ff", fontSize: "20px" }} />
            <span>Confirm Recruiter Removal</span>
          </div>
        }
        open={deleteConfirmModal.visible}
        onCancel={() =>
          setDeleteConfirmModal({
            visible: false,
            recruiterId: null,
            recruiterName: "",
          })
        }
        footer={[
          <Button
            key="cancel"
            onClick={() =>
              setDeleteConfirmModal({
                visible: false,
                recruiterId: null,
                recruiterName: "",
              })
            }
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            onClick={confirmDeleteRecruiter}
          >
            Yes, Remove
          </Button>,
        ]}
      >
        <div className="py-3">
          <p className="text-sm text-gray-800 mb-2">
            Are you sure you want to remove <strong>{deleteConfirmModal.recruiterName}</strong> from this rule?
          </p>
          <p className="text-xs text-gray-500 mb-0">
            This recruiter has <strong>0 active leads</strong> under this rule. Once removed, click <strong>"Update Rule"</strong> to save your changes.
          </p>
        </div>
      </Modal>
    </Dashboard>
  );
};

export default RuleEngineCreate;
